import React, { useState, useEffect, useRef } from "react";
import { Epg, Layout } from "planby";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Import hooks
import { useApp } from "./useApp";

// Import components
import { Timeline, ChannelItem, ProgramItem } from "./components";
import LiveDetailsModal from "./LiveDetailsModal";
import { ReactComponent as RightArrow } from "../../assets/Icons/rightArrow.svg";

const EpgSkeletonLoader = ({ screenWidth }) => {
  const isMobile = window.innerWidth <= 768;
  return (
    <SkeletonTheme baseColor="rgba(255, 255, 255, 0.08)" highlightColor="rgba(255, 255, 255, 0.18)">
      <div
        className="epg-skeleton-wrapper epg-skeleton-header"
        style={{
          width: "100%",
          minHeight: "auto",
          backgroundColor: "#121212",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: isMobile ? "12px" : "24px",
          padding: isMobile ? "75px 12px 12px 12px" : "100px 4% 15px 4%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* 1. Video Player Skeleton (First on mobile: order 1 / top) */}
        <div
          className="skeleton-video-side"
          style={{
            width: isMobile ? "100%" : "45%",
            maxWidth: isMobile ? "100%" : "480px",
            height: isMobile ? "180px" : "250px",
            aspectRatio: "16 / 9",
            borderRadius: isMobile ? "0px" : "12px",
            overflow: "hidden",
            marginTop: "0px",
            alignSelf: "center",
            order: isMobile ? 1 : 2,
          }}
        >
          <Skeleton height="100%" style={{ height: "100%", aspectRatio: "16 / 9", borderRadius: isMobile ? 0 : 12 }} borderRadius={isMobile ? 0 : 12} />
        </div>

        {/* 2. Info Skeleton (Second on mobile: order 2 / bottom) */}
        <div
          className="skeleton-info-side"
          style={{
            width: isMobile ? "100%" : "50%",
            maxWidth: isMobile ? "100%" : "50%",
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            justifyContent: isMobile ? "space-between" : "flex-start",
            alignItems: isMobile ? "center" : "flex-start",
            gap: "10px",
            order: isMobile ? 2 : 1,
          }}
        >
          {isMobile ? (
            <>
              <div>
                <Skeleton width={80} height={12} borderRadius={4} />
                <Skeleton width={160} height={20} borderRadius={6} />
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <Skeleton width={70} height={26} borderRadius={14} />
              </div>
            </>
          ) : (
            <>
              <Skeleton width={110} height={16} borderRadius={4} />
              <Skeleton width={240} height={38} borderRadius={6} />
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", margin: "8px 0" }}>
                <Skeleton width="40%" height={14} borderRadius={4} />
                <Skeleton width="50%" height={14} borderRadius={4} />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                <Skeleton width={85} height={32} borderRadius={6} />
                <Skeleton width={110} height={32} borderRadius={6} />
              </div>
            </>
          )}
        </div>
      </div>
    </SkeletonTheme>
  );
};

function LiveEpgContainer({ selectedCategory }) {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const { isLoading, getEpgProps, getLayoutProps, channelLength, channels, onScrollLeft } = useApp({ selectedCategory, screenWidth });
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const containerRef = useRef(null);
  const scrollableRef = useRef(null);
  const timelineRef = useRef(null);
  const scrollCheckIntervalRef = useRef(null);

  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const pubid = projectInfo?.projectConfig?.pubid;
  const buttonColor = projectInfo?.projectConfig?.config?.EPG_PRIMARY_COLOR || projectInfo?.projectConfig?.config?.BACKGROUND_COLOR;
  const buttonSelectionColor = projectInfo?.projectConfig?.config?.EPG_SECONDARY_COLOR || projectInfo?.projectConfig?.config?.BUTTON_SELECTION_COLOR;
  const textColor = projectInfo?.projectConfig?.config?.TEXT_COLOR;

  const hexToRgba = (hex, opacity = 1) => {
    if (!hex) return '';
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(char => char + char).join('');
    const num = parseInt(c, 16);
    if (isNaN(num)) return hex;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const scrollTimeline = (direction) => {
    if (!scrollableRef.current) return;
    if (screenWidth <= 480) return;

    const scrollAmount = direction === 'left'
      ? -Math.min(scrollableRef.current.clientWidth * 0.5, 600)
      : Math.min(scrollableRef.current.clientWidth * 0.5, 1200);

    scrollableRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });

    setTimeout(checkScrollPosition, 400);
  };

  const checkScrollPosition = () => {
    if (!scrollableRef.current) return;
    if (screenWidth <= 480) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollableRef.current;
    const maxScroll = scrollWidth - clientWidth;

    setShowLeftArrow(scrollLeft > 5);

    setShowRightArrow(maxScroll > 0 && scrollLeft < maxScroll - 5);
  };

  const findScrollableElement = () => {
    if (!containerRef.current) return;

    const selectors = [
      '.planby [class*="TimelineScroll"]',
      '.planby [class*="ScrollBox"]',
      '.css-tv2j2u-ScrollBox',
      '.planby [style*="overflow-x: auto"]',
      '.planby [style*="overflow-x:auto"]',
      '.planby [style*="overflow: auto"]',
      '.planby-epg-container > div > div'
    ];

    for (const selector of selectors) {
      const elements = containerRef.current.querySelectorAll(selector);

      for (const element of elements) {
        if (element && element.scrollWidth > element.clientWidth) {
          scrollableRef.current = element;

          element.addEventListener('scroll', checkScrollPosition);

          checkScrollPosition();
          return true;
        }
      }
    }

    const deepFind = (element) => {
      if (!element) return false;

      if (element.scrollWidth > element.clientWidth &&
        (getComputedStyle(element).overflowX === 'auto' ||
          getComputedStyle(element).overflowX === 'scroll')) {
        scrollableRef.current = element;
        element.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition();
        return true;
      }

      for (let i = 0; i < element.children.length; i++) {
        if (deepFind(element.children[i])) {
          return true;
        }
      }

      return false;
    };

    return deepFind(containerRef.current);
  };

  useEffect(() => {
    if (isLoading) return;
    if (screenWidth <= 480) return;

    let attempts = 0;
    const maxAttempts = 10;

    const findScrollableInterval = setInterval(() => {
      if (attempts >= maxAttempts || findScrollableElement()) {
        clearInterval(findScrollableInterval);
      }
      attempts++;
    }, 300);

    scrollCheckIntervalRef.current = setInterval(checkScrollPosition, 1000);

    return () => {
      clearInterval(findScrollableInterval);
      if (scrollCheckIntervalRef.current) {
        clearInterval(scrollCheckIntervalRef.current);
      }

      if (scrollableRef.current) {
        scrollableRef.current.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [isLoading, pubid, screenWidth]);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        if (!scrollableRef.current) {
          findScrollableElement();
        }
        checkScrollPosition();
      }, 500);
    }
  }, [isLoading, filteredChannels, screenWidth, pubid]);

  useEffect(() => {
    if (!isLoading && onScrollLeft) {
      onScrollLeft();

      if (screenWidth > 480) {
        window.epgScrollLeft = () => scrollTimeline('left');
        window.epgScrollRight = () => scrollTimeline('right');
        window.forceEpgScroll = (direction) => scrollTimeline(direction);
      }

      setTimeout(() => {
        checkScrollPosition();
      }, 1000);
    }

    return () => {
      window.epgScrollLeft = null;
      window.epgScrollRight = null;
      window.forceEpgScroll = null;
    };
  }, [isLoading, onScrollLeft, pubid, screenWidth]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (selectedCategory) {
      const updatedChannels = channels.filter(channel =>
        channel?.categories?.some(category => category?.category_id === selectedCategory?.category_id)
      );
      setFilteredChannels(updatedChannels);
    } else {
      setFilteredChannels(channels);
    }


  }, [selectedCategory, channels]);

  const location = useLocation();
  const selectedChannelData = useSelector((state) => state?.selectedChannel?.value);

  const scrollToSelectedChannel = () => {
    if (!containerRef.current) return;

    const selectedEl = containerRef.current.querySelector('.selected-epg-channel') ||
      containerRef.current.querySelector('.epg-channel-box.selected-epg-channel') ||
      containerRef.current.querySelector('.selected-epg-program-box');

    if (selectedEl) {
      let scrollParent = selectedEl.parentElement;
      while (scrollParent && scrollParent !== containerRef.current) {
        const overflowY = window.getComputedStyle(scrollParent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          break;
        }
        scrollParent = scrollParent.parentElement;
      }

      const targetParent = scrollParent || containerRef.current;
      const parentRect = targetParent.getBoundingClientRect();
      const selectedRect = selectedEl.getBoundingClientRect();
      const relativeTop = selectedRect.top - parentRect.top + targetParent.scrollTop - 60;

      targetParent.scrollTo({
        top: Math.max(0, relativeTop),
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (isLoading) return;
    const timer = setTimeout(scrollToSelectedChannel, 400);
    return () => clearTimeout(timer);
  }, [isLoading, location.search, selectedChannelData, selectedCategory]);

  const handleShowClick = (showData) => {
    setSelectedShow(showData);
    setShowModal(true);
  };

  const showArrows = pubid?.toString() !== "50120" && screenWidth > 480;

  if (isLoading) {
    return <EpgSkeletonLoader screenWidth={screenWidth} />;
  }

  return (
    <div className="liveEpgContainer">
      <div className="epg-container-with-controls">
        <div
          className={`epg-main-container kalingo pubid-50183-container`}
          style={{
            ...(buttonColor ? {
              '--epg-btn-color': buttonColor,
              '--epg-btn-color-hover': hexToRgba(buttonColor, 0.6),
              '--epg-btn-color-bg': hexToRgba(buttonColor, 0.2),
              '--epg-btn-color-border': hexToRgba(buttonColor, 0.4),
            } : {}),
            ...(buttonSelectionColor ? {
              '--epg-selection-color': buttonSelectionColor,
            } : {}),
            ...(textColor ? {
              '--epg-text-color': textColor,
            } : {})
          }}
        >
          {showArrows && (
            <button
              id="epg-scroll-left-btn"
              onClick={() => scrollTimeline('left')}
              className={`arrow-btn left epg-arrow-btn show${!showLeftArrow ? ' disabled' : ''}`}
              disabled={!showLeftArrow}
            >
              <span><RightArrow /></span>
            </button>
          )}

          <div
            ref={containerRef}
            style={{
              height: pubid?.toString() !== "50120" ? "80vh" : channelLength <= 9 ? "100vh" : "190vh",
              width: "100%",
              position: 'relative'
            }}
            id="epg-outer-container"
            className="epg-wrapper"
          >
            <Epg
              isLoading={isLoading}
              isInitialScrollToNow={true}
              initialScrollOffsetMinutes={-660}
              className="planby-epg-container"
              loader={<EpgSkeletonLoader />}
              {...getEpgProps()}
            >
              <Layout
                {...getLayoutProps({ channels: filteredChannels })}
                renderTimeline={(props) => <Timeline {...props} />}
                renderProgram={({ program, ...rest }) => (
                  <ProgramItem key={program.data.id} program={program} {...rest} onShowClick={handleShowClick} />
                )}
                renderChannel={({ channel }) => (
                  <ChannelItem channel={channel} />
                )}
              />
            </Epg>
          </div>
          {showArrows && (
            <button
              id="epg-scroll-right-btn"
              onClick={() => scrollTimeline('right')}
              className={`arrow-btn right epg-arrow-btn show${!showRightArrow ? ' disabled' : ''}`}
              disabled={!showRightArrow}
            >
              <span><RightArrow /></span>
            </button>
          )}
        </div>
      </div>
      {showModal && (
        <LiveDetailsModal show={selectedShow} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

export default LiveEpgContainer;
